import { i18n } from '@lingui/core'
import { t, Trans } from '@lingui/macro'
import { Grid } from '@mui/material'
import { useField } from 'formik'
import { isNumber } from 'lodash'
import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react'
import { useSelector } from 'react-redux'
import '../CKEditor/ckeditor5.css'
import { ErrorLabel } from '../forms/ErrorLabel'
import { CKEditor } from './CKEditor'

interface TypeProps {
  defaultValue?: string
}

interface MuBag {
  useMultiuser?: boolean
  [key: string]: unknown
}

interface FormikCKEditorProps {
  disabled?: boolean
  name: string
  label?: unknown
  config?: unknown
  onChange?: (event: { target: { value: string } }) => void
  onBlur?: (event: unknown) => void
  wordLimit?: unknown
  muBag?: MuBag
  wordCount?: boolean
  maxCharacters?: number
  allowAddingTables?: boolean
  typeProps?: TypeProps
  [key: string]: unknown
}

export const FormikCKEditor = ({
  disabled,
  name,
  label,
  config,
  onChange,
  onBlur,
  wordLimit,
  muBag,
  wordCount,
  maxCharacters,
  allowAddingTables,
  typeProps = {},
  ...props
}: FormikCKEditorProps): ReactElement => {
  const { defaultValue } = typeProps
  const [field, meta, helpers] = useField<string>(name)
  const { value = '' } = field
  const [initialValueSet, setInitialValueSet] = useState(false)
  const [timer, setTimer] = useState<ReturnType<typeof setTimeout> | null>(null)
  const initialInputValue = value.length > 0 ? value : (defaultValue ?? '')
  const [inputValue, setInputValue] = useState(initialInputValue)
  const [stats, setStats] = useState({ words: 0, characters: 0 })
  const ref = useRef<{ editor: any } | null>(null)

  const user = useSelector((state: any) => state.user as {
    language?: string
    userId?: string
  })
  const langVersion = user.language
  const { setValue } = helpers

  const helperArray: ReactNode[] = []
  if (wordCount === true) {
    helperArray.push(
      <span key='wordsCount'>
        {stats.words} <Trans>words</Trans>
        {'. '}
      </span>
    )
  }

  useEffect(() => {
    setInitialValueSet(false)
  }, [name])

  useEffect(() => {
    const hasInitialValue = value.length > 0 || (defaultValue?.length ?? 0) > 0
    if (hasInitialValue && !initialValueSet) {
      // This handles initial value
      setInputValue(value.length > 0 ? value : (defaultValue ?? ''))
      setInitialValueSet(true)
    }
  }, [value, initialValueSet, defaultValue])

  const inputChanged = (): void => {
    const editor = ref.current?.editor
    if (editor == null) {
      return
    }

    const newValue = editor.getData()
    const charCount = editor.plugins.get('WordCount').characters as number
    let hasError = false
    console.log(maxCharacters, charCount)
    if (typeof maxCharacters === 'number' && maxCharacters < charCount) {
      hasError = true
      const max = maxCharacters
      helpers.setError(i18n._(
        t`This fields length cannot exceed ${max} characters!`
      ))
    }
    setInputValue(newValue)
    clearTimeout(timer ?? undefined)
    if (!hasError) {
      const newTimer = setTimeout(() => {
        if (onChange != null) {
          onChange({ target: { value: newValue } })
        }
        void setValue(newValue)
      }, 700)
      setTimer(newTimer)
    }
  }

  const displayError = meta.touched && Boolean(meta.error)

  return (
    <div>
      <CKEditor
        editorRef={ref}
        allowAddingTables={allowAddingTables}
        langVersion={langVersion}
        additionalConfig={{
          wordCount: {
            onUpdate: stats => {
              setStats(stats)
            }
          }
        }}
        data={inputValue}
        disabled={disabled}
        onChangeWithFormik={inputChanged}
        onBlur={(e, editor) => {
          if (onBlur != null) {
            onBlur(e)
          }
          if (!meta.touched) {
            void helpers.setTouched(true)
          }
        }}
        onFocus={(e, editor) => {}}
      />
      <Grid
        container
        className='form-input-helper-text'
        sx={{
          marginTop: '8px'
        }}
      >
        <Grid item xs>
          {displayError ? <ErrorLabel label={meta.error} /> : helperArray}
        </Grid>
        {isNumber(maxCharacters) &&
          <Grid item sx={{ color: 'black !important', textAlign: 'right' }}>
            <span key='limit'>
              <span>
                {`${stats.characters} `}
                <Trans>chars</Trans>
                {' ('}
                <Trans>max</Trans>: {maxCharacters} <Trans>chars</Trans>
                )
              </span>
              {'. '}
            </span>
          </Grid>}
      </Grid>
    </div>
  )
}
